// Small, dependency-free writers for the bulk datasheet download: a PDF with one full-page JPEG per page,
// and a zip archive (stored, not compressed: JPEG pages don't compress further).

const enc = new TextEncoder();

/** A PDF whose pages are JPEG images filling an A4 page (595.28 × 841.89 pt). */
export interface PdfText { xMm: number; yMm: number; size: number; text: string; align?: "left" | "right" | "center"; colour?: string }
export function jpegPdf(pages: Array<{ jpeg: Uint8Array; width: number; height: number; landscape?: boolean; texts?: PdfText[] }>): Uint8Array {
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (b: Uint8Array | string) => { const x = typeof b === "string" ? enc.encode(b) : b; parts.push(x); length += x.length; };
  const obj = (n: number, body: () => void) => { offsets[n] = length; push(`${n} 0 obj\n`); body(); push("\nendobj\n"); };
  const W = 595.28, H = 841.89;
  const pageCount = pages.length;
  // Objects: 1 catalog, 2 pages, then each distinct image once (pages passing the same jpeg share it), then page + contents.
  const images = [...new Set(pages.map((p) => p.jpeg))];
  const imageNo = new Map(images.map((j, i) => [j, 3 + i]));
  const firstPage = 3 + images.length;
  push("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");
  obj(1, () => push("<< /Type /Catalog /Pages 2 0 R >>"));
  const kids = pages.map((_, i) => `${firstPage + i * 2} 0 R`).join(" ");
  // Text drawn over the page image (running header / footer with page numbers), in Helvetica.
  const fontNo = firstPage + pageCount * 2;
  obj(2, () => push(`<< /Type /Pages /Kids [${kids}] /Count ${pageCount} >>`));
  for (const j of images) {
    const p = pages.find((x) => x.jpeg === j)!;
    obj(imageNo.get(j)!, () => {
      push(`<< /Type /XObject /Subtype /Image /Width ${p.width} /Height ${p.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${j.length} >>\nstream\n`);
      push(j);
      push("\nendstream");
    });
  }
  const pt = (mm: number) => (mm / 25.4) * 72;
  const pdfStr = (t: string) => t.replace(/[^\x20-\x7e\u00a0-\u00ff]/g, (c) => ({ "–": "-", "—": "-", "·": "\xb7", "’": "'", "‘": "'", "“": '"', "”": '"', "…": "..." } as Record<string, string>)[c] ?? "?").replace(/[\\()]/g, "\\$&");
  const width = (t: string, size: number) => t.length * size * 0.5;
  pages.forEach((p, i) => {
    const pageNo = firstPage + i * 2, contentNo = pageNo + 1, img = imageNo.get(p.jpeg)!;
    const [pw, ph] = p.landscape ? [H, W] : [W, H];
    obj(pageNo, () => push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pw} ${ph}] /Resources << /XObject << /Im${img} ${img} 0 R >> /Font << /F1 ${fontNo} 0 R >> >> /Contents ${contentNo} 0 R >>`));
    let draw = `q ${pw} 0 0 ${ph} 0 0 cm /Im${img} Do Q`;
    for (const t of p.texts || []) {
      const s = pdfStr(t.text), x = pt(t.xMm) - (t.align === "right" ? width(s, t.size) : t.align === "center" ? width(s, t.size) / 2 : 0);
      draw += `\nBT /F1 ${t.size} Tf ${t.colour || "0.42 0.45 0.5"} rg ${x.toFixed(2)} ${(ph - pt(t.yMm)).toFixed(2)} Td (${s}) Tj ET`;
    }
    const bytes = Uint8Array.from(draw, (c) => c.charCodeAt(0) & 0xff);
    obj(contentNo, () => { push(`<< /Length ${bytes.length} >>\nstream\n`); push(bytes); push("\nendstream"); });
  });
  obj(fontNo, () => push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>"));
  const xref = length;
  const total = fontNo + 1;
  let table = `xref\n0 ${total}\n0000000000 65535 f \n`;
  for (let n = 1; n < total; n++) table += `${String(offsets[n]).padStart(10, "0")} 00000 n \n`;
  push(`${table}trailer\n<< /Size ${total} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return concat(parts, length);
}

// The zip writer is shared with the server (Export to accounting).
export { zip } from "../../shared/zip.js";

function concat(parts: Uint8Array[], length: number): Uint8Array {
  const out = new Uint8Array(length);
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}
